/**
 * Per-category PICK GEOMETRY (P6-clean — docs/bim-plan/08 Part 0): the audit found
 * planPick.js and marquee.js enumerating shipped categories inline, which made every
 * new category unpickable/un-marquee-able until two shared algorithms were edited.
 * Now the algorithms are generic and THIS registry holds the per-category facts:
 *
 *   body(el, model)        → the element's plan primitive (the emphasis vocabulary):
 *                            { kind:'corridor', pts, widthFt } | { kind:'ring', pts }
 *                            | { kind:'spot', pt, rFt } | null
 *   planPriority           → overlap ordering, lower wins (opening > wall > slab —
 *                            the shipped planPick PRIORITY values, which deliberately
 *                            differ from 3D pickRank: plan picking ranks railings with
 *                            openings and stairs with walls)
 *   pickReachFt?(el)       → corridor half-width override for HIT TESTING only
 *                            (railing: the emphasis corridor is a thin 0.5ft band but
 *                            the shipped pick reach was a full 0.5ft — preserved)
 *   pickLevelId(el, model) → which level the element picks on (hosted → the host's)
 *   edgeOnly?              → a 'ring' body picks only NEAR ITS BORDER, never from inside
 *                            (EDGE-ONLY PICK, 2026-10-01 — slabs, roofs, terrain)
 *   zExtent(el, model)     → [zLow, zHigh] world feet (marquee's 3D vertical rule)
 *
 * Adding a category = ONE entry here (or registerPickConfig from its own module).
 */
import {
  centerlineOffsetFt, offsetPolyline, resolveElement, hostedFootprintCorners,
  objectFootprintCorners, columnProfileRing, flattenPathPts, stairLayoutForPath,
  isCeilingFixtureKind, isPointFixtureKind, isGroundFixtureKind, stairVerticalExtent, stairStepInputs, stairSketchSolved,
  pavingSpec, caseworkRunCommands,
} from '@aj/bim-kernel'
import { objectZSpan, objectBaseZ } from '@aj/bim-geometry/categories/object/generate3d'
import { RUG_KINDS } from '@aj/bim-kernel'
import { fixtureZSpan } from '@aj/bim-geometry/categories/fixture/generate3d'
import { columnZRange } from '@aj/bim-geometry/categories/column/generate3d'
import { solveRoofForElement } from '@aj/bim-geometry/categories/roof/solve'
import { projectToPolyline, polylinePointAt } from '../gizmos/handleConfigs/index.js'

/** Wall BODY centered on the derived centerline, not the stored reference (2.6a).
 *  CURVES round fix: flattened, so a bulge edge's selection emphasis/hit-test body
 *  follows the true curve instead of the chord between its stored nodes. */
function wallBodyPts(el) {
  const path = el.params.path
  if (!path || !path.nodes) return null
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const centerPath = delta === 0 ? path : (offsetPolyline(path, delta) || path)
  return flattenPathPts(centerPath)
}

/** Opening anchor: hostOffsetFt runs along the host's STORED path; the visual
 *  position shifts into the host's offset body. */
function openingAnchor(host, hostOffsetFt) {
  const pts = host.params.path.nodes.map((n) => n.pt)
  const { pt, dir } = polylinePointAt(pts, hostOffsetFt || 0)
  const delta = centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly)
  if (delta === 0) return pt
  return [pt[0] - dir[1] * delta, pt[1] + dir[0] * delta]
}

const ownLevel = (el) => el.levelId
const hostLevel = (el, model) => {
  const host = el.hostId ? resolveElement(model, el.hostId) : null
  return host ? host.levelId : null
}

const wallCfg = {
  planPriority: 1,
  pickLevelId: ownLevel,
  body(el) {
    const pts = wallBodyPts(el)
    if (!pts || pts.length < 2) return null
    return { kind: 'corridor', pts, widthFt: el.assemblyThicknessFt || 0.5 }
  },
  zExtent(el) {
    const z0 = (el.level?.elevationFt ?? 0) + (el.params.baseOffsetFt || 0)
    // TOP-LEVEL round (2026-08-10): a level-associated top reads its level + offset
    const topLevel = el.params.topMode === 'level' ? el.paramLevels?.topLevelId : undefined
    const z1 = topLevel
      ? topLevel.elevationFt + (el.params.topOffsetFt || 0)
      : z0 + (el.params.unconnectedHeightFt || 9)
    return [z0, z1]
  },
}

// door operations whose plan symbol actually swings a leaf past the wall face —
// sliders/pockets/multiSlide/cornerSlider/casedOpening never extend past the
// rough opening, so their emphasis needs no help from the block below.
const SWING_OPS = new Set(['hinged', 'pivot', 'dutch', 'french', 'bifold', 'multiBifold', 'gateSwing'])

// door/window only since the FREESTANDING round (2026-08-19) — casework moved to
// caseworkCfg below (the object anatomy: no host, no hostedFootprintCorners).
const hostedCfg = {
  planPriority: 0,
  pickLevelId: hostLevel,
  body(el, model) {
    const host = el.hostId ? resolveElement(model, el.hostId) : null
    if (!host?.params.path?.nodes) return null
    // full-body rectangle (the real footprint — the same corners the snap engine
    // targets, Matt r6 item A); spot fallback if the footprint can't derive
    const corners = hostedFootprintCorners(host, el)
    if (corners) return { kind: 'ring', pts: corners }
    const pt = openingAnchor(host, el.params.hostOffsetFt)
    const w = el.params.widthFt || 3
    return { kind: 'spot', pt, rFt: w / 2 }
  },
  // SELECTION HIGHLIGHT round (Matt, 2026-09-20: "the highlight does not
  // encompass the full object, leaving out the frames... including the door
  // panel and swing line"): `body` above is a tight grab affordance sized to
  // the rough opening — correct for hit-testing, but it left a swinging door's
  // whole leaf + arc unhighlighted. For an operation that actually swings a
  // leaf into the room (SWING_OPS), the emphasis ring extends the same
  // rectangle by the leaf's own length on BOTH sides of the wall — a plain,
  // direction-agnostic pad rather than replicating swingLeaf's exact
  // hinge/facing math, so it's never wrong-sided regardless of hand/facing.
  // Every other operation (sliders, pockets, cased openings…) never draws past
  // the wall face, so the rough-opening rectangle already IS its full extent.
  emphasisBody(el, model) {
    const host = el.hostId ? resolveElement(model, el.hostId) : null
    if (!host?.params.path?.nodes) return null
    const op = String(el.typeParams?.operation || 'hinged')
    if (el.category !== 'door' || !SWING_OPS.has(op)) return hostedCfg.body(el, model)
    const hostPts = flattenPathPts(host.params.path)
    const s = el.params.hostOffsetFt || 0
    const leafLen = el.params.widthFt || 3
    const t = host.assemblyThicknessFt || 0
    const delta = centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly)
    const atOff = (ss, off) => {
      const { pt, dir } = polylinePointAt(hostPts, ss)
      return [pt[0] - dir[1] * off, pt[1] + dir[0] * off]
    }
    const s0 = Math.max(0, s - leafLen / 2), s1 = s + leafLen / 2
    const offA = delta - t / 2 - leafLen, offB = delta + t / 2 + leafLen
    return { kind: 'ring', pts: [atOff(s0, offA), atOff(s0, offB), atOff(s1, offB), atOff(s1, offA)] }
  },
  zExtent(el, model) {
    const host = el.hostId ? resolveElement(model, el.hostId) : null
    const zHost = (host?.level?.elevationFt ?? el.level?.elevationFt ?? 0) + (host?.params.baseOffsetFt || 0)
    // sillFt (2026-08-09): height above the host's base — doors and windows both
    // resolve it now (schema default 0 for doors, 3 for windows preserves the
    // pre-existing pick box for every unraised element of either category)
    const sill = el.params.sillFt || 0
    return [zHost + sill, zHost + sill + (el.params.heightFt || 6.67)]
  },
}

const runBody = (el) => {
  const path = el.params.path
  if (!path || !path.nodes || path.nodes.length < 2) return null
  const w = el.category === 'railing' || el.category === 'fence' ? 0.5 : (el.params.widthFt || 3.5)
  return { kind: 'corridor', pts: flattenPathPts(path), widthFt: w }
}
const stairCfg = {
  planPriority: 1,
  pickLevelId: ownLevel,
  // STAIR round (audit item 2): the hover/selection/hit body follows the TRUE run
  // from the layout solver — a drawn path longer than the riser count used to
  // highlight a ghost region with no geometry in it. U-STAIR round: the body is
  // the solver's real footprint RING (legs + landing hulls walked into one
  // boundary) — a corridor over a doubled-back centerline painted overlapping
  // ribbons. Corridor over usedPts only as fallback; raw path if no layout at all.
  body(el) {
    const path = el.params.path
    if (!path || !path.nodes || path.nodes.length < 2) return null
    // SKETCHED STAIRS: the footprint is the sketch's two sides joined by the end risers
    if (el.category === 'stair') {
      const sk = stairSketchSolved(el.params)
      if (sk) return { kind: 'ring', pts: [...sk.edges[0], ...[...sk.edges[1]].reverse()] }
    }
    const w = el.params.widthFt || 3.5
    // ramp's rampCfg spreads this body() too but keeps its own (non-mode) riseFt
    // schema — stairVerticalExtent's 'offset'/'rise' fallback branches (baseMode/
    // topMode absent) reproduce the exact same numbers for a ramp element
    const extent = el.category === 'stair' ? stairVerticalExtent(el) : null
    const { maxRiserFt, treadDepthFt } = stairStepInputs(el.params, extent ? extent.zTop - extent.zBase : null)
    const floorToFloorFt = extent ? Math.max(extent.zTop - extent.zBase, maxRiserFt) : (el.params.floorToFloorFt || 10)
    const layout = stairLayoutForPath(path, {
      widthFt: w,
      floorToFloorFt,
      maxRiserFt,
      treadDepthFt,
    }, el.params)
    if (!layout) return runBody(el)
    if (layout.outline) return { kind: 'ring', pts: layout.outline }
    return { kind: 'corridor', pts: layout.usedPts, widthFt: w }
  },
  zExtent(el) {
    const extent = stairVerticalExtent(el)
    if (extent) return [extent.zBase, extent.zTop]
    const z = (el.level?.elevationFt ?? 0) + (el.params.baseOffsetFt || 0)
    return [z, z + (el.params.floorToFloorFt || 10)]
  },
}
const rampCfg = {
  ...stairCfg,
  zExtent(el) {
    const z = (el.level?.elevationFt ?? 0) + (el.params.baseOffsetFt || 0)
    return [z, z + (el.params.riseFt || 2)]
  },
}
const railingCfg = {
  planPriority: 0,
  pickLevelId: ownLevel,
  body: runBody,
  pickReachFt: () => 0.5,   // shipped reach — wider than the thin emphasis band
  zExtent(el) {
    const z = (el.level?.elevationFt ?? 0) + (el.params.baseOffsetFt || 0)
    return [z, z + (el.params.heightFt || 3)]
  },
}


// FACE-EDGE KERNEL K1 (docs/bim-plan/21): a body's plan grab is the convex hull
// of its vertices' xy footprint — coarse but honest for selection/marquee (the
// Eraser does its own exact edge hit-test via editing/bodyPick.js); exact
// concave picking rides the K2 face-pick round. z band = the topology's true
// z-range (absolute model feet — dissociated from the level datum by doctrine).
function bodyHull2(topo) {
  const pts = Object.values(topo.vertices).map((p) => [p.x, p.y])
  if (pts.length < 3) return pts
  pts.sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const cr = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower = []
  for (const p of pts) {
    while (lower.length >= 2 && cr(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cr(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)]
}
const bodyCfg = {
  planPriority: 2,
  pickLevelId: ownLevel,
  body: (el) => {
    const topo = el.params.topology
    if (!topo || !topo.vertices) return null
    const hull = bodyHull2(topo)
    if (hull.length >= 3) return { kind: 'ring', pts: hull }
    if (hull.length === 2) return { kind: 'corridor', pts: hull, widthFt: 0.5 }
    return null
  },
  zExtent(el) {
    const topo = el.params.topology
    if (!topo || !topo.vertices) return [0, 0]
    let lo = Infinity
    let hi = -Infinity
    for (const p of Object.values(topo.vertices)) {
      if (p.z < lo) lo = p.z
      if (p.z > hi) hi = p.z
    }
    if (!Number.isFinite(lo)) return [0, 0]
    return [lo, Math.max(hi, lo + 0.1)]
  },
}

const slabBody = (el) => {
  const boundary = el.params.boundary
  if (!boundary || !boundary.nodes || boundary.nodes.length < 3) return null
  return { kind: 'ring', pts: flattenPathPts(boundary) }
}
// EDGE-ONLY PICK (2026-10-01, Matt: "Selecting roofs, floors, ceilings, and topo terrains
// should only be possible by clicking their edges, folds, contours, borders … not their
// faces. Having the faces be selectable is causing editing in 2d views to be difficult"):
// their ring body picks only within reach of its border; their drawn folds / contours pick
// through PlanView's linework hit (symbolHits). Marquee still uses the full ring.
const slabCfg = (planPriority, zExtent, zGated = false) => ({
  planPriority,
  edgeOnly: true,
  pickLevelId: ownLevel,
  body: slabBody,
  zExtent,
  zGated,
})
// GL2 Phase D (docs/bim-plan/12 §3.2) — the flatZ degenerate-band fix: floors,
// ceilings, and roofs used to return [z, z], an interval no crossing/intersection
// test can ever pass — the sharpest visible-but-unpickable case (a slab drawn by
// the projected pass on another level's plan refused every pick). Slabs now carry
// their true band from the assembly thickness (floors hang below their top,
// ceilings build up from their base — slabSolid's own convention); roofs resolve
// their real solved z-range below.
const floorZ = (el) => {
  const zTop = (el.level?.elevationFt ?? 0) + (el.params.heightOffsetFt || 0)
  return [zTop - (el.assemblyThicknessFt || 0.5), zTop]
}
const ceilingZ = (el) => {
  const zBase = (el.level?.elevationFt ?? 0) + (el.params.heightOffsetFt || 0)
  return [zBase, zBase + (el.assemblyThicknessFt || 0.5)]
}
// Roofs: the true solved band (plate line to ridge + thickness). solveRoofForElement
// is pure planar math (no clipper) and only runs for CROSS-LEVEL tests — the home
// branch of pickLevelMatch short-circuits before zExtent is ever called — so the
// hover-path cost is a few solves per model, only while working across levels.
const roofZ = (el) => {
  try {
    const solved = solveRoofForElement(el)
    if (solved?.faces?.length) {
      let lo = Infinity, hi = -Infinity
      for (const f of solved.faces) {
        for (const p of f.ring) {
          const z = f.zAt(p)
          if (z < lo) lo = z
          if (z > hi) hi = z
        }
      }
      if (isFinite(lo) && isFinite(hi)) return [lo, hi + (el.assemblyThicknessFt || 1)]
    }
  } catch { /* unsolvable boundary — fall through to the flat band */ }
  const z = (el.level?.elevationFt ?? 0) + (el.params.plateHeightFt || 0)
  return [z, z + (el.assemblyThicknessFt || 1)]
}

// ELECTRICAL FIXTURES round: hosted body (the shared face-footprint ring — the same
// corners the snap engine targets) with the fixture's own z span: the plain
// instance mount height ± half the kind's body height.
// CEILING HOSTING round (2026-08-23): recessedCan/pendant/flushMount host into a
// ceiling instead — the OBJECT anchor model (objectFootprintCorners reads
// el.params.path/rotationDeg + width/depth, same as a real object) and z off the
// host CEILING's own heightOffsetFt (the slabCfg#ceilingZ convention), not a wall's
// baseOffsetFt.
const fixtureCfg = {
  planPriority: 0,
  // a ceiling light may be UNHOSTED (2026-08-24 migration hardening) — its own
  // levelId is the fallback identity; wall kinds always resolve their host's
  pickLevelId: (el, model) => hostLevel(el, model) ?? el.levelId,
  body(el, model) {
    if (isPointFixtureKind(el.typeParams?.fixtureKind)) {
      const corners = objectFootprintCorners(el)
      return corners ? { kind: 'ring', pts: corners } : null
    }
    return hostedCfg.body(el, model)
  },
  zExtent(el, model) {
    const host = el.hostId ? resolveElement(model, el.hostId) : null
    const span = fixtureZSpan(el.params, el.typeParams || {})
    // LIGHT STYLES: a ground light's span already carries its Base Height — off its own level
    if (isGroundFixtureKind(el.typeParams?.fixtureKind)) {
      const zl = el.level?.elevationFt ?? 0
      return [zl + span.z0, zl + span.z1]
    }
    if (isCeilingFixtureKind(el.typeParams?.fixtureKind)) {
      // hosted: the ceiling's own z; unhosted: the migration fallback height
      // (must stay byte-identical with fixtureSolid's UNHOSTED_CEILING_LIGHT_FT
      // read, or what renders can't be picked)
      const zHost = host?.level
        ? host.level.elevationFt + (host.params.heightOffsetFt || 0)
        : (el.level?.elevationFt ?? 0) + UNHOSTED_CEILING_LIGHT_FT
      return [zHost + span.z0, zHost + span.z1]
    }
    const zHost = (host?.level?.elevationFt ?? el.level?.elevationFt ?? 0) + (host?.params.baseOffsetFt || 0)
    return [zHost + span.z0, zHost + span.z1]
  },
}

// OBJECTS round: freestanding — footprint ring from the shared kernel helper, z span
// from the kind (rim-mounted sinks hang from rimHeightFt; everything else sits on the
// base). Priority 0: a toilet on a floor slab must win the overlap like openings do.
// SURFACE HOSTING round (2026-08-24): a hosted object's z-bounds follow the SAME
// hostSurfaceTopZ derivation objectSolid uses (imported, never re-copied) — pick/
// hover/selection stay consistent with wherever the object actually draws.
const cupolaCfg = {
  pickLevelId: ownLevel,
  body(el) {
    const n = el.params.path?.nodes?.[0]
    if (!n) return null
    const rot = ((el.params.rotationDeg || 0) * Math.PI) / 180
    const h = Math.max(1.25, (el.params.sizeMode === 'manual' ? el.params.widthFt : el.params.sizeHintFt) || 3) / 2
    const X = [Math.cos(rot), Math.sin(rot)], Y = [-Math.sin(rot), Math.cos(rot)]
    const at = (x, y) => [n.pt[0] + X[0] * x + Y[0] * y, n.pt[1] + X[1] * x + Y[1] * y]
    return { kind: 'ring', pts: [at(-h, -h), at(h, -h), at(h, h), at(-h, h)] }
  },
  zExtent(el) {
    const z = typeof el.params.seatZFt === 'number' ? el.params.seatZFt : (el.level?.elevationFt ?? 0) + 18
    return [z - 1, z + 10]
  },
}

const objectCfg = {
  // RUGS (2026-10-01): a rug lies under the furniture on it — a click inside a sofa
  // over a rug takes the sofa; the rug still beats walls-free floor (below walls,
  // above slabs)
  planPriority: (el) => (RUG_KINDS.includes(el.typeParams?.objectKind) ? 1.5 : 0),
  pickLevelId: ownLevel,
  body(el) {
    const corners = objectFootprintCorners(el)
    return corners ? { kind: 'ring', pts: corners } : null
  },
  zExtent(el, model) {
    const host = el.hostId ? resolveElement(model, el.hostId) : null
    const { zInst: z, hostTop } = objectBaseZ(el, host)
    const span = objectZSpan(el.typeParams || {}, hostTop != null && (host.category === 'casework' || host.category === 'object'))
    return [z + span.z0, z + span.z1]
  },
}

// FREESTANDING round (2026-08-19): casework — freestanding, the object anatomy.
// Footprint ring from the shared kernel helper (objectFootprintCorners already
// reads WIDTH instance-first, which is casework's own shape); z span from style
// (uppers hang from the type's mountHeightFt, base/tall sit on the floor) —
// unaffected by the host going away. Priority 0, same as object: a cabinet on a
// floor slab wins the overlap like an appliance does.
const caseworkCfg = {
  planPriority: 0,
  pickLevelId: ownLevel,
  body(el) {
    // BAYS AND LAYERS: open wall has nothing to pick
    if (el.typeParams?.style === 'upper' && el.typeParams?.upperCell === 'open') return null
    const corners = objectFootprintCorners(el)
    return corners ? { kind: 'ring', pts: corners } : null
  },
  // CASEWORK AS ONE ITEM (2026-10-09, Matt: "make the whole run this item that we select and
  // move"): hovering or selecting any box lights the WHOLE casework line — its back line to its
  // deepest box, end to end. Picking still lands on the box under the cursor.
  emphasisBody(el, model) {
    const runId = el.params.runId
    const f = model && typeof runId === 'string' && runId ? caseworkRunCommands.runFrame(model, runId) : null
    if (!f || f.members.length < 2) return this.body(el)
    let d = 0
    for (const m of f.members) d = Math.max(d, resolveElement(model, m.id)?.typeParams?.depthFt || 2)
    const at = (s, t) => [f.start[0] + f.dir[0] * s + f.front[0] * t, f.start[1] + f.dir[1] * s + f.front[1] * t]
    return { kind: 'ring', pts: [at(0, 0), at(f.lengthFt, 0), at(f.lengthFt, d), at(0, d)] }
  },
  zExtent(el) {
    const zBase = el.level?.elevationFt ?? 0
    const mount = String(el.typeParams?.style) === 'upper' ? (el.typeParams?.mountHeightFt || 4.5) : 0
    // heightFt is a nullable INSTANCE override shadowing the type's key — read
    // el.params first, falling back to el.typeParams, never el.typeParams alone.
    const h = el.params.heightFt ?? el.typeParams?.heightFt ?? 3
    return [zBase + mount, zBase + mount + h]
  },
}

// STRUCTURE round. Column: profile ring from the shared kernel helper (the same ring
// the snap engine and the prism outline use), z from the base/top-level resolution —
// priority 0 so a post inside a wall corridor or on a slab wins the overlap like
// openings do. Beam: a width corridor along the path, z = the datum-anchored top
// span (top = level + offsets, depth down).
// Z-GATED HOME PICK (2026-08-24 audit — the same gap as floor/ceiling): unlike
// beam/roof/stair/railing/casework/trim (all projLines-based, which draw
// something at home level unconditionally, any z), a column with no projLines
// only draws within real z-bounds even at home level (plan.ts's STRUCT_CATS
// branches — below-cut-in-depth, cut-crossing, or above-cut-in-reach; a column
// entirely below the current View Depth, or entirely out of an RCP's upward
// reach, draws nothing). columnZRange is plain arithmetic (no geometry solve,
// unlike roofZ) so gating costs nothing extra on every hover.
const columnCfg = {
  planPriority: 0,
  pickLevelId: ownLevel,
  zGated: true,
  body(el) {
    const ring = columnProfileRing(el)
    return ring ? { kind: 'ring', pts: ring } : null
  },
  zExtent(el) {
    const zr = columnZRange(el)
    return zr ? [zr.z0, zr.z1] : [0, 0]
  },
}
const beamCfg = {
  planPriority: 1,
  pickLevelId: ownLevel,
  body(el) {
    const path = el.params.path
    if (!path || !path.nodes || path.nodes.length < 2) return null
    return { kind: 'corridor', pts: flattenPathPts(path), widthFt: el.params.widthFt || 0.3 }
  },
  zExtent(el) {
    const elev = el.level?.elevationFt ?? 0
    const zs = elev + (el.params.startOffsetFt || 0)
    const ze = elev + (el.params.endOffsetFt || 0)
    return [Math.min(zs, ze) - (el.params.depthFt || 1), Math.max(zs, ze)]
  },
}

// TOPO round (addendum 2 — selectable from every view): the plan hit body is the
// point set's convex hull (a cheap monotone chain — no triangulation needed for
// picking). WORST priority: anything sitting on the terrain wins the overlap; the
// ground is the always-there fallback candidate, reachable via Tab cycling.
function topoHull(points) {
  const pts = points.map((p) => p.pt).slice().sort((a, b) => a[0] - b[0] || a[1] - b[1])
  if (pts.length < 3) return null
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)]
}
const topoCfg = {
  planPriority: 6,
  edgeOnly: true,
  pickLevelId: ownLevel,
  body(el) {
    const points = Array.isArray(el.params.points) ? el.params.points : []
    const hull = topoHull(points)
    if (hull) return { kind: 'ring', pts: hull }
    // a nascent terrain (1–2 points, no hull yet) picks at its visible point
    // markers — without this the drawn × marks were unclickable dead pixels
    if (points.length) {
      const cx = points.reduce((s, p) => s + p.pt[0], 0) / points.length
      const cy = points.reduce((s, p) => s + p.pt[1], 0) / points.length
      const r = Math.max(2, ...points.map((p) => Math.hypot(p.pt[0] - cx, p.pt[1] - cy) + 1))
      return { kind: 'spot', pt: [cx, cy], rFt: r }
    }
    return null
  },
  zExtent(el) {
    const points = Array.isArray(el.params.points) ? el.params.points : []
    if (!points.length) return [0, 0]
    let lo = Infinity, hi = -Infinity
    for (const p of points) {
      if (p.zFt < lo) lo = p.zFt
      if (p.zFt > hi) hi = p.zFt
    }
    const depth = el.params.baseDepthFt > 0 ? el.params.baseDepthFt : 5
    return [lo - depth, hi]
  },
}

// POOL DESIGNER round: a pool picks anywhere over its water (it has no thin face to
// fight a 2D edit the way a slab does) — its band runs from the deep floor to the coping
const poolZ = (el) => {
  const zTop = (el.level?.elevationFt ?? 0) + (el.params.heightOffsetFt || 0)
  const deep = el.params.deepDepthFt ?? el.typeParams?.defaultDeepFt ?? 6
  return [zTop - deep - 2, zTop]
}
// PAVING (2026-10-08): the slab's own corridor (curbs included); under walls, posts and
// fixtures (priority 3, the slab/pool tier). z band = the profile's range, slab to curb.
const pavingCfg = {
  planPriority: 3,
  pickLevelId: ownLevel,
  body(el) {
    const spec = pavingSpec(el)
    if (!spec) return null
    if (spec.street) {
      // a STREET's cross section sits off to one side of its flowline: the corridor is the
      // flowline moved to the section's middle (per-vertex averaged normals)
      const st = spec.street
      const off = (st.side * (st.uMin + st.uMax)) / 2
      const P = spec.pts
      const nrm = (i) => {
        const a = P[Math.max(0, i - 1)], b = P[Math.min(P.length - 1, i + 1)]
        const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
        return [-(b[1] - a[1]) / l, (b[0] - a[0]) / l]
      }
      return { kind: 'corridor', pts: P.map((p, i) => { const n = nrm(i); return [p[0] + n[0] * off, p[1] + n[1] * off] }), widthFt: spec.widthFt }
    }
    const curb = spec.edge === 'curb' ? 2 * spec.curbWidthFt : 0
    return { kind: 'corridor', pts: spec.pts, widthFt: spec.widthFt + curb }
  },
  zExtent(el) {
    const spec = pavingSpec(el)
    const z = el.level?.elevationFt ?? 0
    if (!spec) return [z - 0.5, z + 0.5]
    const lo = Math.min(spec.startZ, spec.endZ, ...spec.breaks.map((b) => b.zFt))
    const hi = Math.max(spec.startZ, spec.endZ, ...spec.breaks.map((b) => b.zFt))
    if (spec.street) return [lo - 1.5, hi + 1.5]
    return [lo - spec.thicknessFt - 1, hi + (spec.edge === 'curb' ? spec.curbHeightFt : 0) + 0.5]
  },
}
const poolCfg = { planPriority: 3, pickLevelId: ownLevel, body: slabBody, zExtent: poolZ, zGated: true }
// DOWNSPOUTS (2026-10-04): picked at its OUTLET on the gutter (the stored anchor) — the
// pipe itself derives from the roof, so the plan pick stays a cheap spot; in 3D the
// mesh picks it anywhere along its length
const downspoutCfg = {
  planPriority: 0,
  pickLevelId: ownLevel,
  body(el) {
    const pt = el.params.path?.nodes?.[0]?.pt
    return pt ? { kind: 'spot', pt, rFt: 0.9 } : null
  },
  zExtent(el) {
    const elev = el.level?.elevationFt ?? 0
    return [elev - 30, elev + 60]
  },
}

const PICK_CONFIGS = {
  pool: poolCfg,
  downspout: downspoutCfg,
  wall: wallCfg,
  door: hostedCfg,
  window: hostedCfg,
  // WALL NICHE (2026-10-07): picks by its recess footprint (hostedFootprintCorners' niche branch)
  niche: hostedCfg,
  // AWNINGS (2026-10-08): picks by its hood footprint (hostedFootprintCorners' awning branch)
  awning: hostedCfg,
  // CUPOLAS (2026-10-09): picks by its footprint around the anchor (the placement-time size
  // hint; the 3D view picks the real meshes)
  cupola: cupolaCfg,
  casework: caseworkCfg,
  fixture: fixtureCfg,
  stair: stairCfg,
  ramp: rampCfg,
  railing: railingCfg,
  fence: railingCfg,
  paving: pavingCfg,
  body: bodyCfg,
  // Z-GATED HOME PICK fix (2026-08-24, Matt: "a floor below the cut plan...
  // should [not] be selectable" / "if we can't see something, don't let us
  // select it"): floors and ceilings genuinely draw NOTHING when their
  // z-extent falls outside the view's interval — unlike roof (see roofZ's own
  // comment: draws dashed UNCONDITIONALLY at home level, the r2 drafting
  // convention) or wall/door/stair/column/beam (always draw something —
  // solid, dashed, or cut). `zGated: true` opts these two into the SAME
  // interval test cross-level content already gets, closing the one gap
  // pickLevelMatch's home-level short-circuit left open.
  floor: slabCfg(2, floorZ, true),
  ceiling: slabCfg(3, ceilingZ, true),
  roof: slabCfg(4, roofZ),
  object: objectCfg,
  column: columnCfg,
  beam: beamCfg,
  topo: topoCfg,
}

export function pickConfigFor(category) {
  return PICK_CONFIGS[category] || null
}
export function registerPickConfig(category, cfg) {
  PICK_CONFIGS[category] = cfg
}

/**
 * CROSS-LEVEL SELECTION round, generalized by GL2 Phase D (docs/bim-plan/12 §3.2 —
 * VISIBLE ⇒ EDITABLE): an element is part of a plan view's content — pickable/
 * hoverable/Tab-cyclable/marquee-able/snap-contributing — iff it's home to that
 * level OR its z-extent INTERSECTS the view's z-interval [viewDepthZ, cutZ] (the
 * same interval the renderer selects by, so what the view shows is what it edits).
 * `cutZ` is the view's cut-plane height in absolute world feet (levelElevationFt +
 * cutHeightFt) — null/undefined skips the cross-level check entirely (today's exact
 * home-level-only behavior, used by the 3D/section surfaces which have no cut
 * plane). `viewDepthZ` is the interval's bottom (cutZ − depthFt, defaulting to
 * levelElev − ε) — null keeps the pre-D crosses-the-plane test, so callers that
 * haven't threaded a view depth behave exactly as before. Categories with no
 * `zExtent` keep home-level-only behavior. ONE predicate, every plan-surface call
 * site. The story underlay never reaches this predicate at all — it has no pick
 * path by construction (ghost, snap-only; see PlanView.jsx).
 */
export function pickLevelMatch(cfg, el, model, levelId, cutZ, viewDepthZ = null) {
  // bim-plan/14 r5 (LIVE SITE PLAN): a LEVEL-FREE plan passes levelId = null — the
  // home-level short-circuit must not fire (an orphaned hosted element's null
  // pickLevelId would match null-for-null), so everything resolves through the
  // interval test, which with cutZ = the view's anchor (∞ when derived-above-all)
  // and viewDepthZ = −∞ is exactly the level-free "everything below the cut" range.
  const isHome = levelId != null && cfg.pickLevelId(el, model) === levelId
  // Z-GATED HOME PICK fix (2026-08-24, Matt: "we are able to select a lot of
  // things not strictly in the view... if we can't see something, don't let
  // us select it"): home-level content used to bypass the interval test
  // ENTIRELY — correct for categories that always draw something at home
  // (wall/door/stair/roof/column/beam — solid, dashed, or cut, per each
  // category's own plan.ts convention) but wrong for `cfg.zGated` categories
  // (floor/ceiling) that draw NOTHING when out of range. Falls through to the
  // same interval test cross-level content already gets.
  if (isHome && !cfg.zGated) return true
  if (cutZ == null || !cfg.zExtent) return isHome
  const [zLow, zHigh] = cfg.zExtent(el, model)
  if (viewDepthZ == null) return zLow < cutZ && zHigh > cutZ
  return zLow < cutZ && zHigh > viewDepthZ
}
